import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Loader2, AlertCircle, Pin, Bell, Eye, ChevronLeft, ChevronRight, List } from 'lucide-react';
import { noticeApi, NoticeDetail } from '@/api/noticeApi';

export default function NoticeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isPublicPage = location.pathname.startsWith('/notices');
  const noticeBasePath = isPublicPage ? '/notices' : '/dashboard/notices';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<NoticeDetail | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    noticeApi.getDetail(Number(id))
      .then(res => setNotice(res.data))
      .catch(() => setError('공지사항을 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  }, [id]);

  const formatDateTime = (iso: string) =>
    iso ? new Date(iso).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-';

  if (loading) {
    return (
      <div className="flex justify-center items-center py-40">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 md:p-8 max-w-3xl mx-auto">
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!notice) return null;

  const pageContent = (
    <div className="space-y-6 p-4 md:p-8 max-w-3xl mx-auto">
      {/* 헤더 */}
      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold">공지사항</h1>
        </div>
      </div>

      <Card>
        <CardHeader className="border-b pb-4">
          {!isPublicPage && (
            <div className="flex items-start gap-2 flex-wrap mb-2">
              {notice.isPinned && (
                <Badge className="text-blue-600 border border-blue-300 bg-blue-50 rounded-full">
                  <Pin className="w-3 h-3 mr-1" />고정
                </Badge>
              )}
              {notice.isBanner && (
                <Badge className="text-orange-600 border border-orange-300 bg-orange-50 rounded-full">
                  <Bell className="w-3 h-3 mr-1" />배너
                </Badge>
              )}
            </div>
          )}
          <CardTitle className="text-xl leading-relaxed">{notice.title}</CardTitle>
          <div className="flex items-center gap-4 text-sm text-gray-500 mt-2">
            <span>관리자</span>
            <span>{formatDateTime(notice.createdAt)}</span>
            <span className="flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" />{(notice.viewCount ?? 0).toLocaleString()}
            </span>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div
            className="notice-html text-gray-800"
            style={{ minHeight: '200px' }}
            dangerouslySetInnerHTML={{ __html: notice.content }}
          />
        </CardContent>
      </Card>

      {/* 이전글 / 다음글 */}
      <Card>
        <CardContent className="p-0">
          {notice.prevNotice && (
            <div
              className="flex items-center gap-3 px-4 py-3 border-b hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => navigate(`${noticeBasePath}/${notice.prevNotice!.id}`)}
            >
              <ChevronLeft className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-xs text-gray-400 w-12 flex-shrink-0">이전글</span>
              <span className="text-sm text-gray-700 hover:text-primary truncate">{notice.prevNotice.title}</span>
            </div>
          )}
          {notice.nextNotice && (
            <div
              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => navigate(`${noticeBasePath}/${notice.nextNotice!.id}`)}
            >
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-xs text-gray-400 w-12 flex-shrink-0">다음글</span>
              <span className="text-sm text-gray-700 hover:text-primary truncate">{notice.nextNotice.title}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 목록 버튼 */}
      <div className="flex justify-center">
        <Button variant="outline" onClick={() => navigate(noticeBasePath)}>
          <List className="w-4 h-4 mr-2" />
          목록으로
        </Button>
      </div>
    </div>
  );

  if (isPublicPage) {
    return (
      <>
        <Helmet>
          <title>{notice?.title ? `${notice.title} - ALT Generation 공지사항` : '공지사항 - ALT Generation'}</title>
          <meta name="description" content={notice?.title ? `${notice.title} - ALT Generation 공지사항` : 'ALT Generation 공지사항 상세 내용을 확인하세요.'} />
          <link rel="canonical" href={`https://alt.aigeneration-tech.com/notices/${id}`} />
        </Helmet>
        <div className="min-h-screen flex flex-col">
          <Header />
          <main className="flex-1">{pageContent}</main>
          <Footer />
        </div>
      </>
    );
  }

  return pageContent;
}
